import { useState } from "react";
import { NativeSelect } from "@mantine/core";
import { callFunction } from "../api/client";
import type { Campaign } from "../api/types";
import { useUserAccounts } from "../lib/campaign";
import { recentDate } from "../lib/format";
import { Dialog } from "../ui/Dialog";

/* Assigning a campaign, reassigning it, or leaving it with no owner.
 *
 * One person owns a campaign at a time, and a campaign moves from one person
 * to the next (Nestor, September 25). The choices are the people who
 * can sign in (api.user_account), and the engine keeps every stint and who
 * made each change (api.assign_campaign, engine migration 214). The button
 * says what will happen: Assign, Reassign, or Remove the owner.
 * docs/plans/campaign-assignment.md, milestone 7. */
export function OwnerDialog({ token, c, opened, onClose, onDone }: {
  token: string;
  c: Campaign;
  opened: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const accounts = useUserAccounts(token);
  const current = c.owner_id ?? "";
  const [chosen, setChosen] = useState(current);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const label = chosen === "" ? "Remove the owner" : current ? "Reassign" : "Assign";
  // An owner whose account was turned off is not among the people who can
  // sign in, and still owns the campaign until it is reassigned.
  const people = (accounts.data ?? []).map((u) => ({ value: u.id ?? "", label: u.label ?? u.email ?? "" }));
  if (current && accounts.data && !people.some((p) => p.value === current)) {
    people.unshift({ value: current, label: `${c.owner_name ?? "The owner"} (cannot sign in)` });
  }
  const save = async () => {
    setBusy(true); setError(null);
    try {
      await callFunction(token, "assign_campaign", { p_name: c.name, p_owner: chosen || null });
      onDone();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog opened={opened} title={current ? `Reassign ${c.label}` : `Assign ${c.label}`} onClose={onClose}
            action={{ label, onPress: save }} busy={busy} disabled={chosen === current || accounts.isLoading}
            error={error ?? (accounts.isError ? (accounts.error as Error).message : null)}>
      <NativeSelect
        label="Owner" value={chosen} data-autofocus
        description={current && c.owner_since ? `${c.owner_name} since ${recentDate(c.owner_since)}.` : undefined}
        data={[{ value: "", label: "No owner" }, ...people]}
        onChange={(e) => setChosen(e.currentTarget.value)}
      />
      {/* Every campaign in a sequence has one owner, so a new owner takes it
          out (engine migration 311). Moving it into another rep's sequence
          is Put in a sequence. */}
      {chosen !== current && (c.sequence_length ?? 1) > 1
        ? <p className="muted">This takes it out of its sequence. To move it into another person's sequence, use Move in its sequence.</p>
        : null}
    </Dialog>
  );
}
